import { EyeOutlined, ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import ObjectSelect from "@components/ObjectSelect";
import { AIAnalysisModal } from "@features/ai-analysis/AIAnalysisModal";
import { useAnalysisList, useLatestAnalysis, useRunAnalysis } from "@features/ai-analysis/api";
import { FindingsBadges } from "@features/ai-analysis/FindingsBadges";
import type { AIAnalysis, AIHighlight } from "@features/ai-analysis/types";
import { useAnalysisPolling } from "@features/ai-analysis/useAnalysisPolling";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { resolveUserRole } from "@shared/rbac";
import { useStoredUser } from "@shared/useStoredUser";
import { App as AntdApp, Button, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

const statusMap: Record<string, { color: string; label: string }> = {
  success: { color: "green", label: "Готов" },
  partial: { color: "orange", label: "Частичный" },
  error: { color: "red", label: "Ошибка" },
  empty: { color: "default", label: "Нет данных" },
  pending: { color: "blue", label: "В процессе" },
};

const parseHighlights = (raw?: string | null): AIHighlight[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const AIAnalysisPage = () => {
  const { message } = AntdApp.useApp();
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const user = useStoredUser();
  const role = resolveUserRole(user);
  const canRun = role !== "viewer";

  const regionId = selectedObject?.regionId;
  const objectId = selectedObject?.objectId;

  const objectName = useMemo(() => {
    if (!selectedObject) return "";
    const obj = objects.find(
      (o) => o.n_ob === selectedObject.objectId && o.syb_rnk === selectedObject.regionId,
    );
    return obj?.txt ?? `Объект ${selectedObject.objectId}`;
  }, [objects, selectedObject]);

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const listQuery = useAnalysisList(regionId, objectId, page, pageSize);
  const latestQuery = useLatestAnalysis(regionId, objectId);

  const [pollingId, setPollingId] = useState<string>();
  const pollingQuery = useAnalysisPolling(pollingId);

  const [modalAnalysis, setModalAnalysis] = useState<AIAnalysis | null>(null);

  const runMutation = useRunAnalysis();

  const handleRun = useCallback(() => {
    if (!regionId || !objectId) return;
    runMutation.mutate(
      { region_id: regionId, object_id: objectId },
      {
        onSuccess: (res) => {
          if (res.cached) {
            message.info(`Актуальный анализ уже есть: ${objectName}`);
          } else {
            message.info(`Анализ запущен: ${objectName}`);
            setPollingId(res.id);
          }
        },
        onError: () => {
          message.error("Не удалось запустить анализ");
        },
      },
    );
  }, [regionId, objectId, objectName, runMutation, message.error, message.info]);

  // Stop polling on terminal status or timeout
  useEffect(() => {
    if (pollingQuery.isTimeout) {
      setPollingId(undefined);
      message.warning("Анализ выполняется дольше обычного. Обновите список через минуту.");
      listQuery.refetch();
      return;
    }
    if (!pollingQuery.data) return;
    const st = pollingQuery.data.status;
    if (st === "success" || st === "partial") {
      setPollingId(undefined);
      latestQuery.refetch();
      listQuery.refetch();
    } else if (st === "error" || st === "empty") {
      setPollingId(undefined);
      listQuery.refetch();
    }
  }, [
    pollingQuery.data?.status,
    pollingQuery.isTimeout,
    listQuery.refetch,
    latestQuery.refetch,
    message.warning,
    pollingQuery.data,
  ]);

  // Reset on object change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on object change
  useEffect(() => {
    setPollingId(undefined);
    setModalAnalysis(null);
    setPage(1);
  }, [regionId, objectId]);

  // Freshness for refresh button
  const isFresh = useMemo(() => {
    if (!latestQuery.data?.created_at) return false;
    return dayjs().diff(dayjs(latestQuery.data.created_at), "hour") < 24;
  }, [latestQuery.data?.created_at]);

  const hoursUntilRefresh = useMemo(() => {
    if (!latestQuery.data?.created_at) return 0;
    return Math.max(0, 24 - dayjs().diff(dayjs(latestQuery.data.created_at), "hour"));
  }, [latestQuery.data?.created_at]);

  const columns: ColumnsType<AIAnalysis> = [
    {
      title: "Дата",
      dataIndex: "created_at",
      width: 140,
      render: (v: string) => (v ? dayjs(v).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Статус",
      dataIndex: "status",
      width: 110,
      render: (status: string) => {
        const cfg = statusMap[status] ?? { color: "default", label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Находки",
      dataIndex: "highlights",
      ellipsis: true,
      render: (_: unknown, record: AIAnalysis) => {
        const hl = parseHighlights(record.highlights);
        if (!hl.length) return <span style={{ color: "#bfbfbf" }}>—</span>;
        return <FindingsBadges highlights={hl} />;
      },
    },
    {
      title: "Модель",
      dataIndex: "model",
      width: 130,
      ellipsis: true,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_: unknown, record: AIAnalysis) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setModalAnalysis(record)}
        />
      ),
    },
  ];

  return (
    <PageLayoutWithHelp title="ИИ-анализ">
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space wrap>
          <ObjectSelect
            objects={objects}
            value={selectedObject}
            onChange={setSelectedObject}
            style={{ minWidth: 280 }}
          />
          {canRun && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleRun}
              loading={runMutation.isPending || !!pollingId}
            >
              Запустить анализ
            </Button>
          )}
          {canRun && latestQuery.data && (
            <Tooltip title={isFresh ? `Доступно через ${hoursUntilRefresh} ч` : "Обновить анализ"}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRun}
                disabled={isFresh || runMutation.isPending}
              />
            </Tooltip>
          )}
        </Space>

        <Table<AIAnalysis>
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          rowKey="id"
          size="small"
          loading={listQuery.isLoading}
          pagination={{
            current: page,
            pageSize,
            total: listQuery.data?.total ?? 0,
            onChange: setPage,
            size: "small",
            showSizeChanger: false,
          }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => setModalAnalysis(record),
          })}
        />
      </Space>

      <AIAnalysisModal
        analysis={modalAnalysis}
        open={!!modalAnalysis}
        onClose={() => setModalAnalysis(null)}
      />
    </PageLayoutWithHelp>
  );
};

export default AIAnalysisPage;
